import { FmdGoodOutlined, ChatBubbleOutlineOutlined, CalendarMonthOutlined, BookmarkOutlined } from '@mui/icons-material'
import { Grid, Avatar, Typography, Collapse, Button, Chip, List, ListItem } from '@mui/material'
import moment from 'moment'
import { useMemo, useState, Dispatch, SetStateAction } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useGetJobQuery } from '../../../Redux/api/jobApi'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetClientQuery } from '../../../Redux/api/clientApi'
import { KaziAvatar } from '../../../Shared Components/Kazi/KaziAvatar'
import { KaziBookmark } from '../../../Shared Components/Kazi/KaziBookmark'
import { useAppDispatch, useAppSelector } from '../../../Redux/store'
import { JobApplyModal } from './JobApplyModal'
import { formatPaymentInfo, getPayRate } from '../../../Utils/String'
import { useGetTalentQuery } from '../../../Redux/api/talentApi'
import { Job } from '../../../Types/Job'
import { Client } from '../../../Types/Client'
import { useUser } from '../../../Hooks/useUser'
import { setShowUnverifiedModal } from '../../../Redux/slices/GlobalModalSlice'

interface IViewPostingForm {
  job: Job;
  client: Client;
  mode: 'view' | 'edit';
  setMode: Dispatch<SetStateAction<'view' | 'edit'>>;
}

export const ViewPosting = ({job, client, mode, setMode}:IViewPostingForm) => {
    /* ******************** Hooks ******************** */
    const navigate = useNavigate();
    const userType = useAppSelector( state => state.Global.userType);
    const userId = useAppSelector( state => state.Global.userId);
    const dispatch = useAppDispatch();
    const { isVerified } = useUser();
    /* ******************** Variables ******************** */
    const [open, setOpen] = useState(false);
    const postedByUser = useMemo(() =>{
        return userId === client?.locator
    }, [client]);
    /* ******************** Functions ******************** */
    const handleMessage = () => {
        navigate(`/messages?conversation=${job?.client_locator}`)
    }
    const handleApply = () => {
        if (isVerified) {
            setOpen(true)
            return
        }
        dispatch(setShowUnverifiedModal(true));
    }
    /* ******************** Effects ******************** */
    /* ******************** JSX ******************** */
    return (
        <>
            <Grid container display={"flex"} flexDirection={"row"} sx={{color: 'primary.main'}} columnGap={15}>
                <Grid item flex={1} display={"flex"} flexDirection={"column"} gap={4}>
                    <Grid item display={"flex"} flexDirection={"column"} gap={'16px'}>
                        <Typography variant='lora-headline-small' fontWeight={"bold"}>{job?.title ?? "N/A"}</Typography>
                        <Grid item display={"flex"} flexDirection={"row"} gap={1}>
                            <CalendarMonthOutlined />
                            <Typography variant='title-small'>{formatPaymentInfo(job)}</Typography>
                        </Grid>
                        <Grid item display={"flex"} flexDirection={"row"} gap={1}>
                            <FmdGoodOutlined />
                            <Typography variant='title-small'>{job?.work_site}</Typography>
                        </Grid>
                        <Grid item display={"flex"} flexDirection={"row"} gap={1}>
                            <ChatBubbleOutlineOutlined />
                            <Typography variant='title-small'>{job?.primary_language}</Typography>
                        </Grid>
                    </Grid>
                    <Grid item display={"flex"} flexDirection={"column"} wrap='wrap'>
                        <Typography variant='lora-title-medium' fontWeight={"bold"} marginBottom={'16px'}>Job description</Typography>
                        <Typography variant='body-medium'>{job?.description}</Typography>
                    </Grid>
                    <Grid item display={"flex"} flexDirection={"column"} >
                        <Typography variant='lora-title-medium' fontWeight={"bold"} marginBottom={'16px'}>Responsibilities</Typography>      
                        <List sx={{ listStyleType: 'disc', pl: 3 }} disablePadding>
                            {job?.responsibilities.map(resp => (
                                <ListItem key={resp} sx={{ display: 'list-item' }} disablePadding>
                                    <Typography variant='body-medium' >
                                        {resp}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>  
                    </Grid>
                    {/* Deliverables - Only show for Short-term jobs */}
                    {job?.type === "Short-term" && job?.deliverables && job.deliverables.length > 0 && (
                        <Grid item display={"flex"} flexDirection={"column"} >
                            <Typography variant='lora-title-medium' fontWeight={"bold"} marginBottom={'16px'}>Deliverables</Typography>      
                            <List sx={{ listStyleType: 'disc', pl: 3 }} disablePadding>
                                {job?.deliverables.map(deliverable => (
                                    <ListItem key={deliverable} sx={{ display: 'list-item' }} disablePadding>
                                        <Typography variant='body-medium' >
                                            {deliverable}
                                        </Typography>
                                    </ListItem>
                                ))}
                            </List>  
                        </Grid>
                    )}
                    <Grid item display={"flex"} flexDirection={"column"} marginBottom={4}>
                        <Typography variant='lora-title-medium' fontWeight={"bold"} marginBottom={'16px'}>Qualifications</Typography>      
                        <List sx={{ listStyleType: 'disc', pl: 3 }} disablePadding>
                            {job?.skills.map(qual => (
                                <ListItem key={qual.skill} sx={{ display: 'list-item' }} disablePadding>
                                    <Typography variant='body-medium' >
                                        {qual.skill}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List> 
                    </Grid>
                </Grid>
                <Grid item flex={1} display={"flex"} flexDirection={"column"} gap={4}>
                    <Grid item display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} marginBottom={13}>
                            <Grid display={'flex'} flexDirection={'row'}>
                                <Grid>
                                    {userType === 'talent' && <KaziBookmark locator={job?.locator ?? ''} sx={{padding: '4px 8px'}}/>}
                                </Grid>
                                <Grid>
                                    {
                                        postedByUser ?
                                        <Button variant={'contained'} size='large' onClick={() => setMode('edit')}>Edit</Button>:
                                        (
                                            userType === 'talent' ? 
                                            (
                                                job?.talent_locator === userId ?
                                                <Button variant={'contained'} size='large' onClick={handleMessage}>Message</Button>:
                                                (
                                                    job?.applicants.some( a => a === userId)?
                                                    <Button 
                                                        variant={'contained'} 
                                                        size='large' 
                                                        disabled 
                                                        sx={{
                                                            "&.Mui-disabled": {
                                                                backgroundColor: 'black.50',
                                                                border: 'none',
                                                                color: 'primary.main'
                                                            }
                                                        }}
                                                    >
                                                        Applied
                                                    </Button> :
                                                    <Button variant={'contained'} size='large' onClick={handleApply}>Apply</Button>
                                                )
                                            ) : null
                                        )
                                    }
                                </Grid>
                            </Grid > 
                    </Grid>
                    <Grid item display={"flex"} flexDirection={"column"} >
                        <Typography variant='lora-title-medium' fontWeight={"bold"} marginBottom={'16px'}>Skills</Typography>
                        <Grid item display={"flex"} flexDirection={"row"} flexWrap={'wrap'} gap={1} width={'55%'}>
                            {job?.tags.map((tag)=>(
                                <Chip key={tag} label={tag} variant='outlined'/>
                            ))}
                        </Grid>
                    </Grid>
                    <Grid item display={"flex"} flexDirection={"column"} gap={'16px'}>
                        <Typography variant='lora-title-medium' fontWeight={"bold"} >About</Typography>
                        <KaziAvatar
                            locator={client?.locator ?? ''}
                            first_name={client?.first_name ?? ''}
                            last_name={client?.last_name ?? ''}
                            width={'40px'}
                        />
                        <Typography>{client?.description ? client.description.substring(0,157) +  '...' : ''}</Typography>
                        <Button variant='outlined' sx={{width: 'fit-content'}} size='large' onClick={() => navigate(`/profile/client/${client?.locator}`)}>Learn more</Button>
                    </Grid>
                </Grid>
            </Grid>  
            <JobApplyModal
                open={open}
                setOpen={setOpen}
                posting={job}
                clientInfo={{
                locator: client?.locator!,
                first_name: client?.first_name!,
                last_name: client?.last_name!,
                profile_picture: client?.profile_picture!,
                company: client?.company!,
                }}
            />
    </>
  );
};
